import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "./api";
import { PageHeader, Card, Field, Input, SaveBar } from "./components/Field";
import { ImageUpload } from "./components/ImageUpload";
import { usePreview } from "./PreviewContext";

const SETTINGS_FIELDS = [
  { key: "logo_url", label: "Logo", type: "image" },
  { key: "brand_name", label: "Brand name (header)", type: "text" },
  { key: "brand_tagline", label: "Brand tagline", type: "text" },
  { key: "site_title", label: "Site title (browser tab)", type: "text" },
  { key: "site_description", label: "Site description (SEO)", type: "text" },
  { key: "contact_email", label: "Contact email", type: "text" },
  { key: "instagram_url", label: "Instagram URL", type: "text" },
  { key: "square_booking_url", label: "Square booking URL", type: "text" },
  { key: "amazon_book_url", label: "Amazon book URL", type: "text" },
];

export const Settings = () => {
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const { reload: reloadPreview } = usePreview();

  useEffect(() => {
    api.get("/cms/settings").then(({ data: doc }) => {
      setData(doc.data || {});
      setOriginal(JSON.stringify(doc.data || {}));
    });
  }, []);

  const dirty = useMemo(() => data !== null && JSON.stringify(data) !== original, [data, original]);
  if (data === null) return <div className="text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const update = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const onSave = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/cms/settings", { data });
      setData(saved.data);
      setOriginal(JSON.stringify(saved.data));
      toast.success("Settings saved.");
      reloadPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        overline="Site"
        title="Site Settings"
        description="Brand, logo, contact info, and outbound links used across the public site."
      />
      <Card>
        <div className="grid gap-6">
          {SETTINGS_FIELDS.map((f) =>
            f.type === "image" ? (
              <ImageUpload
                key={f.key}
                label={f.label}
                value={data[f.key] || ""}
                onChange={(v) => update(f.key, v)}
                testId={`settings-${f.key}`}
              />
            ) : (
              <Field key={f.key} label={f.label}>
                <Input value={data[f.key] || ""} onChange={(v) => update(f.key, v)} />
              </Field>
            )
          )}
        </div>
      </Card>
      <SaveBar saving={saving} dirty={dirty} onSave={onSave} onReset={() => setData(JSON.parse(original))} />
    </>
  );
};
